import React, { useContext, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import { TSite } from '../../../../contexts/site/site-context';
import { TDeliveryVenue } from '../../../../contexts/delivery-venue/delivery-venue-context';
import { darken, rgba } from 'polished';
import { CTA } from "../../../general/Button";
import { trackSiteSelectComplete } from '../../../../services/siteSelectTracking';

type DeliveryVenueCardProps = {
    deliveryVenue: TDeliveryVenue;
};

const DeliveryVenueCard = ({ deliveryVenue }: DeliveryVenueCardProps) => {
    const navigate = useNavigate();

    const [spinnerShow, setSpinnerShow] = useState(false);
    const [link, setLink] = useState(`/venues/${deliveryVenue.site.handle}`);

    let open = deliveryVenue.channels.venues?.is_open ?? false;

    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // (the button on the card is on it, so its click is this one too)
        trackSiteSelectComplete('page', 'venues', deliveryVenue.site.handle);

        localStorage.setItem('address', JSON.stringify({ deliveryVenueName: deliveryVenue.name, address1: deliveryVenue.address_1, address2: deliveryVenue.address_2, city: deliveryVenue.address_city, postcode: deliveryVenue.address_postcode }));
        localStorage.setItem('postcode', deliveryVenue.address_postcode);
        localStorage.setItem('delivery-venue', deliveryVenue.uid);

        if (open) {
            setSpinnerShow(true);
            navigate(link);
        } else {
            setSpinnerShow(true);
            navigate(`${link}?viewOnly=1`);
        }
    }

    const CtaStr = () => {
        if ( open ) {
            return 'Order delivery';
        } else {
            return 'View menu';
        }
    }

    const CtaLink = () => { return open ? link : link + '?viewOnly=1'; }

    const handleCtaClick = () => {
        setSpinnerShow(true);
        navigate(CtaLink());
    };

    return (
        <StyledCard
            open={open}
            className={`site-card ${open ? 'openRestaurant' : 'closedRestaurant'}`}
            key={'venues-' + deliveryVenue.uid}
        >
            <div className="card__main" tabIndex={1}
                onClick={handleCardClick}
            >
                <Card.Body>
                    <Card.Title>
                        <h2>{deliveryVenue.name}</h2>
                    </Card.Title>
                    <Card.Text as="div">
                        <div className="location">
                            <span>{deliveryVenue.address_1}</span><span className="sm-hide">,</span><span>{deliveryVenue.address_city} {deliveryVenue.address_postcode}</span>
                        </div>
                        {open ? (
                            <div className="openingTimes">
                                <span className="material-symbols-outlined">schedule</span>
                                Currently open
                            </div>
                        ) : (
                            <div className="notAcceptingOrders">
                                <span className="material-symbols-outlined">cancel</span>
                                Not currently accepting orders
                            </div>
                        )}
                    </Card.Text>
                </Card.Body>
                <Card.Footer>
                   <CTAButton open={open} className={`card__btn`} onClick={handleCtaClick}>
                        <span>{ CtaStr() }</span>
                    </CTAButton>
                </Card.Footer>
            </div>
        </StyledCard>
    );
};

const CTAButton = styled(CTA)<{open: Boolean}>`
    display: inline-flex;
    gap: 4px;
    width: 100%;
    color: ${(props) => props.open ? 'var(--ui_primary_cta_label);' : 'var(--text-black)'};
    background: ${(props) => props.open ? 'var(--ui_primary_cta);' : 'var(--bg-disabled)'};
    font-size: 16px;
    line-height: 16px;
    padding: 8px 20px;
    border-radius: var(--radius_l);
    height: 40px;
    border: var(--ui_primary_cta_border);
    transition: background-color 0.15s linear, color 0.15s linear, border-color 0.15s linear;

    ${(props) => !props.open && `
        border-color: var(--bg-disabled);
    `}

    @media (min-width: 768px) {
        width: auto;
    }

    &:hover, &:active {
        background: ${(props) => darken(0.05, props.theme.v4.ui_1)};

        ${(props) => !props.open && `
            color: var(--text-black);
            background: var(--bg-disabled);
            border-color: var(--bg-disabled);
        `}
    }
`;

const StyledCard = styled(Card)<{ open: boolean }>`
    cursor: pointer;
    background: var(--color_ui_7);
    border: var(--ui_card_border);
    border-left-width: 0;
    border-right-width: 0;
    border-radius: 0;
    transition: box-shadow 0.2s ease-in-out;
    overflow: hidden;

    @media screen and (max-width: 767px) {
        &:not(:first-child) {
            border-top-width: 0;
        }
    }

    .card-title {
        display: flex;
        align-items: center;
        margin-bottom: 4px;
        gap: 4px;
        flex-wrap: wrap;

        h2 {
            flex-grow: 1;
            margin-bottom: 0;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
            color: var(--text_subsubheading_color);
        }

        span {
            color: var(--text-grey);
            font-size: 16px;
        }
    }

    .card-body {
        padding: 16px 16px 0 16px;
        font-size: 16px;

        .location {
            display: flex;
            align-items: start;
            font-size: 16px;
            line-height: 24px;
            color: var(--text-grey);
            margin-bottom: 4px;
            span.sm-hide {
                margin-right: 3px;
                @media screen and (max-width: 767px) {
                    display: none;
                }
            }
            @media screen and (max-width: 767px) {
                flex-direction: column;
            }
        }

        .openingTimes,
        .notAcceptingOrders {
            display: flex;
            align-items: start;
            font-size: 16px;
            line-height: 24px;
            font-weight: 500;
            @media screen and (min-width: 1441px) {
                margin-bottom: 8px;
            }
            .material-symbols-outlined {
                padding-right: 4px;
                font-size: 24px;
                line-height: 24px;
            }
        }

        .notAcceptingOrders {
            color: var(--invalid);
            font-weight: 600;
        }
    }

    .card-footer {
        padding: 16px;
        background: transparent;
        border: none;
        display: flex;
        justify-content: flex-start;

        .card__btn {
            @media screen and (min-width: 768px) {
                width: 50%;
                max-width: 278px;
            }
            position: relative;
        }
    }

    @media screen and (min-width: 768px) {
        &:first-child { margin-top: 8px; }
        margin-bottom: 20px;
        border-radius: var(--radius_xl);
        border: var(--ui_card_border);
    }

    ${(props) => props.theme.v4.card_shadow === 'shadow' && `
        @media screen and (min-width: 768px) {
            box-shadow: 0px 4px 10px -4px rgb(0 0 0 / 10%), 0 2px 4px -2px rgba(0 0 0 / 5%), 0 0px 2px rgba(0 0 0 / 5%);
            &:hover { box-shadow: 0 22px 24px 0 rgb(0 0 0 / 8%); }
        }
    `}

    ${(props) => props.theme.v4.card_shadow === 'flat' && `
        @media screen and (min-width: 768px) {
            box-shadow: none;
            &:hover { box-shadow: 0 22px 24px 0 rgb(0 0 0 / 8%); }
        }
    `}
`;

export default DeliveryVenueCard;
